import React from 'react';
import PropTypes from 'prop-types';

// Inject the state-animation keyframes once at module load (no SCSS in this component;
// mirrors the inject-once technique used by OptimiserStatusBadge/index.jsx). The gear's
// spin animates a nested <g> whose local origin (0,0) is the gear centre — the centring
// translate lives on the *parent* <g> as a plain SVG "transform" attribute, kept separate
// so the CSS "transform" animation on the child never clobbers it (CSS transform and the
// SVG transform attribute don't compose on the same element).
if (typeof document !== 'undefined' && !document.getElementById('optimiser-run-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'optimiser-run-style';
  styleEl.textContent = `
    @keyframes optimiser-run-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    .optimiser-run-gear { transform-origin: 0 0; animation: optimiser-run-spin 2.5s linear infinite; }
    /* Idle "Zzz": three letters fade in/out on the same cycle, staggered so they blink
       in sequence rather than all together — reads as a classic sleep indicator. */
    @keyframes optimiser-idle-zzz { 0%,20%{opacity:0} 40%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
    .optimiser-idle-zzz { opacity: 0; animation: optimiser-idle-zzz 2s ease-in-out infinite; }
    .optimiser-idle-zzz--1 { animation-delay: 0s; }
    .optimiser-idle-zzz--2 { animation-delay: 0.3s; }
    .optimiser-idle-zzz--3 { animation-delay: 0.6s; }
  `;
  document.head.appendChild(styleEl);
}

const STATE_META = {
  running: { colour: '#fa8c16', label: 'Optimiser running' },
  queued: { colour: '#fa8c16', label: 'Optimiser run queued' },
  idle: { colour: '#bfbfbf', label: 'Optimiser idle' },
  finished: { colour: '#389e0d', label: 'Optimiser run finished' },
  failed: { colour: '#cf1322', label: 'Optimiser run failed' },
  cancelled: { colour: '#722ed1', label: 'Optimiser run cancelled' },
  unknown: { colour: '#d9d9d9', label: 'Optimiser status unknown' }
};

const GEAR_TOOTH_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

const glyph = (state, colour) => {
  if (state === 'running') {
    // Spinning gear: an open ring (the hub hole keeps it reading as a gear rather
    // than a disc) with eight butt-capped teeth — square tooth ends say "gear",
    // round ones would read as a sun. The continuous rotation carries the
    // "working on it" meaning even at nav-icon size.
    return (
      <g transform="translate(10,10)">
        <g className="optimiser-run-gear">
          <circle cx="0" cy="0" r="3.6" stroke={colour} strokeWidth="2.4" fill="none" />
          {GEAR_TOOTH_ANGLES.map(angle => (
            <line
              key={angle}
              x1="0"
              y1="4.7"
              x2="0"
              y2="6.3"
              transform={`rotate(${angle})`}
              stroke={colour}
              strokeWidth="2.4"
              strokeLinecap="butt"
            />
          ))}
        </g>
      </g>
    );
  }
  if (state === 'finished') {
    // Solid circle with a white tick — the universal "done" mark, echoing the green
    // tick language used elsewhere in the app. Static: a finished run needs no motion.
    return (
      <g>
        <circle cx="10" cy="10" r="7.5" fill={colour} />
        <path d="M6.4 10.4 L9 13 L13.8 7.6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>
    );
  }
  if (state === 'idle') {
    // Crescent moon (lower-left) with the three staggered, fading "Zzz" letters rising
    // to its upper right — sleep/standby. The moon path is the standard feather "moon"
    // in a 24-box, scaled and shifted to leave headroom for the letters.
    return (
      <g>
        <g transform="translate(0.2,6.2) scale(0.55)">
          <path d="M21 12.79 A9 9 0 1 1 11.21 3 A7 7 0 0 0 21 12.79 Z" fill={colour} />
        </g>
        <text x="11.8" y="11" fontSize="5" fill={colour} className="optimiser-idle-zzz optimiser-idle-zzz--1">z</text>
        <text x="13.6" y="8.2" fontSize="6.5" fill={colour} className="optimiser-idle-zzz optimiser-idle-zzz--2">z</text>
        <text x="15.2" y="5.4" fontSize="7" fill={colour} className="optimiser-idle-zzz optimiser-idle-zzz--3">z</text>
      </g>
    );
  }
  if (state === 'failed') {
    // Solid circle with a white cross — "failed"/error, mirroring the finished tick.
    return (
      <g>
        <circle cx="10" cy="10" r="7.5" fill={colour} />
        <path d="M7 7 L13 13 M13 7 L7 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
      </g>
    );
  }
  if (state === 'queued') {
    // Static hollow amber circle — same activity colour family as running, but
    // no motion and no teeth: waiting, not working.
    return <circle cx="10" cy="10" r="6.5" stroke={colour} strokeWidth="2.5" fill="none" />;
  }
  if (state === 'cancelled') {
    // Solid slate-purple circle with a white diagonal slash — "stopped/cancelled",
    // sharing the solid-circle-with-white-mark family of finished (tick) and failed
    // (cross). Static: a cancelled run needs no motion.
    return (
      <g>
        <circle cx="10" cy="10" r="7.5" fill={colour} />
        <path d="M6 14 L14 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
      </g>
    );
  }
  // unknown
  return <circle cx="10" cy="10" r="6" fill={colour} />;
};

// Forwards ref + spreads unrecognised props onto the root <span> so that
// wrapping trigger components (e.g. antd's <Tooltip>) can attach their own
// hover handlers and DOM ref to this element — without it, cloneElement-based
// triggers silently drop onMouseEnter/onMouseLeave and never open.
// No native `title` attribute on the wrapper — this icon is normally hovered inside
// an antd <Tooltip> (see OptimiserStatusBadge/index.jsx and views/OptimiserStatus/page.jsx),
// and a native title attribute on the same element fights that: the browser's own
// OS-rendered title tooltip shows *on top of* antd's styled popup, since native tooltips
// always render above page content. The <svg>'s aria-label below still covers a11y.
const OptimiserRunIcon = React.forwardRef(({ state, size, ...rest }, ref) => {
  const key = STATE_META[state] ? state : 'unknown';
  const meta = STATE_META[key];
  const wrapperStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1
  };
  return (
    <span
      ref={ref}
      className={`optimiser-run-icon optimiser-run-icon--${key}`}
      style={wrapperStyle}
      {...rest}
    >
      <svg width={size} height={size} viewBox="0 0 20 20" aria-label={meta.label} role="img">
        {glyph(key, meta.colour)}
      </svg>
    </span>
  );
});

OptimiserRunIcon.displayName = 'OptimiserRunIcon';

OptimiserRunIcon.propTypes = {
  state: PropTypes.string,
  size: PropTypes.number
};

OptimiserRunIcon.defaultProps = {
  state: 'unknown',
  size: 22
};

export default OptimiserRunIcon;
